/**
 * 场景背景图（豆包出图管线：docs/场景背景图-豆包提示词.md，经 scripts/process_scene_bg.py 出到 web/public/scenes/）
 *
 * 16 键与行程覆盖清单见 docs「场景清单」表；图未就位的键静默回退纸白底。
 * 2026-10-21 从 MonthProgressPanel 抽出：阶段审查演出宿主（`StageStoryPanel`）也要按事件指定底图，
 * 两处各抄一张表，以后加图或改路径必漏一处。
 */
import { SCENE_BG_LQIP } from './sceneLqip';
import { EXTRA_IMAGE_LQIP } from './sceneLqipExtra';

export type SceneKey =
    | 'filmset-modern' | 'filmset-costume' | 'photoshoot' | 'theater' | 'event' | 'music' | 'live'
    | 'audition' | 'gym' | 'study' | 'styling' | 'musicroom' | 'rehearsal' | 'home' | 'vacation'
    | 'home-night' | 'cafe';

export const SCENE_BGS: Record<SceneKey, string> = {
    'filmset-modern': 'scenes/bg-filmset-modern.webp',
    'filmset-costume': 'scenes/bg-filmset-costume.webp',
    photoshoot: 'scenes/bg-photoshoot.webp',
    theater: 'scenes/bg-theater.webp',
    event: 'scenes/bg-event.webp',
    music: 'scenes/bg-music.webp',
    live: 'scenes/bg-live.webp',
    audition: 'scenes/bg-audition.webp',
    gym: 'scenes/bg-gym.webp',
    study: 'scenes/bg-study.webp',
    styling: 'scenes/bg-styling.webp',
    musicroom: 'scenes/bg-musicroom.webp',
    rehearsal: 'scenes/bg-rehearsal.webp',
    home: 'scenes/bg-home.webp',
    'home-night': 'scenes/bg-home-night.webp',   // 夜间卧室（2026-10-26 茄子供图）：审查一三段（立约/通过回响/失败）专用
    vacation: 'scenes/bg-vacation.webp',
    cafe: 'scenes/bg-cafe.webp',                 // 拾光咖啡馆（赴约周末自动演出底图，与自由出行店面共用同图）
};

/**
 * 各场景图 8×8 平均 RGB（2026-10-29 采样，管线同 SCENE_BGS）：**只**用于图不可见期间的容器内联底色，
 * 图可见即清回 `var(--card)`。⛔ 不是 UI 令牌、⛔ 手调「好看一点」；新增场景图必须同加一行
 * （tests/_check_scene_bg_assets.mjs 拦）。
 */
export const SCENE_BG_TINTS: Record<SceneKey, string> = {
    'filmset-modern': '#cfcac8',
    'filmset-costume': '#e6dacf',
    photoshoot: '#e2e1e1',
    theater: '#d6baa4',
    event: '#efeae5',
    music: '#b0bbbe',
    live: '#ece8e6',
    audition: '#f2e8d6',
    gym: '#f5efe5',
    study: '#e1dcd3',
    styling: '#f1efed',
    musicroom: '#e9e3d9',
    rehearsal: '#f4e9dd',
    home: '#f2ece4',
    'home-night': '#d2c3b8',
    vacation: '#e6eaea',
    cafe: '#e6dcd0',
};

/** 当前正在显示的场景图 URL：预热队列据此剔除，避免当前图被 low 队列再抢一次 */
let activeUrl = '';

/** 已发起过请求的 URL（含当前图与失败图）：会话内幂等，刷新才重置 */
const warmed = new Set<string>();

/** 待预热队列；null = 尚未建立（等宿主先 markSceneActive 登记当前图再建） */
let queue: string[] | null = null;
let running = false;
let exhausted = false;

/**
 * 任意全屏底图的 LQIP 查询：先查额外表（小地图/影视城/公园），再按 URL 反查场景表。
 * 找不到返回 undefined（调用方按「无占位」处理，不报错）。
 */
export function lqipFor(url: string): string | undefined {
    const extra = EXTRA_IMAGE_LQIP[url];
    if (extra) return extra;
    const key = (Object.keys(SCENE_BGS) as SceneKey[]).find(k => SCENE_BGS[k] === url);
    return key ? SCENE_BG_LQIP[key] : undefined;
}

/** 图未到位时给容器铺 LQIP（background cover）；图可见后 `clearLqip` 清除 */
export function applyLqip(el: HTMLElement, url: string): void {
    const lqip = lqipFor(url);
    if (!lqip) return;
    el.style.backgroundImage = `url("${lqip}")`;
    el.style.backgroundSize = 'cover';
    el.style.backgroundPosition = 'center top';
    el.style.backgroundRepeat = 'no-repeat';
}

/** 清除 LQIP（回 CSS 自身的底色） */
export function clearLqip(el: HTMLElement): void {
    el.style.backgroundImage = '';
}

/**
 * 铺占位底：主色 + LQIP 模糊小图（内联 base64，零请求）。图 decode 后由 `clearScenePlaceholder` 清掉，
 * ⛔ 常驻第二层背景（UI规范 §5）。目的：等待期玩家看到的是「正在变清楚的一张画面」，不是白屏。
 */
export function applyScenePlaceholder(el: HTMLElement, key: SceneKey): void {
    el.style.backgroundColor = SCENE_BG_TINTS[key] ?? '';
    applyLqip(el, SCENE_BGS[key]);
}

/** 图可见后清除占位底（回 CSS `var(--card)`） */
export function clearScenePlaceholder(el: HTMLElement): void {
    el.style.backgroundColor = '';
    clearLqip(el);
}

/**
 * 贴一张场景底图：当前图以 `high` 优先级请求，`decode()` 完成后才回调（避免解码期白闪）。
 *
 * 不切 `visibility`、不持有过期状态 —— 显示通道与守卫留在宿主，两宿主（月度页 / 阶段审查）共用这一份实现。
 */
export function loadSceneBgImage(img: HTMLImageElement, url: string, done: (ok: boolean) => void): void {
    img.onload = null;
    img.onerror = null;
    // 属性必须先于 src：否则请求已按默认优先级发出，再改 fetchPriority 无效
    img.fetchPriority = 'high';
    img.decoding = 'async';
    img.onload = () => {
        img.onload = null;
        img.onerror = null;
        const finish = (): void => done(true);
        // decode 失败也必须显示，绝不能让面板停在 hidden
        if (typeof img.decode === 'function') {
            img.decode().then(finish, finish);
        } else {
            finish();
        }
    };
    img.onerror = () => {
        img.onload = null;
        img.onerror = null;
        done(false);
    };
    img.src = url;
}

/** 登记当前图：写 activeUrl、标记已发起，并从待预热队列中剔除 */
export function markSceneActive(url: string): void {
    activeUrl = url;
    warmed.add(url);
    if (queue) queue = queue.filter(u => u !== url);
}

function nextIdle(cb: () => void, first: boolean): void {
    if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(cb, { timeout: 2500 });
        return;
    }
    // 无 requestIdleCallback：用定时器节拍近似空闲（首轮多让一点，等当前图先出网）
    setTimeout(cb, first ? 1200 : 900);
}

/**
 * 当前图优先、其余图后台渐进预热。
 *
 * 不变量（2026-10-29 §3.4cm）：`running || exhausted` 幂等返回；每轮空闲最多 `maxConcurrent`（默认 2）张、
 * `low` 优先级、失败不重试；`warmed` 会话内去重，不随面板 destroy 取消（填充的是 URL 级缓存）。
 *
 * ⚠️ 调用方顺序钉死：先 `markSceneActive(url)`、再 `loadSceneBgImage()`、再本函数，且本函数放在同一 tick
 * 的尾部而不进 `load` 回调 —— 否则 404 / 慢网会让预热永远不启动。
 */
export function scheduleScenePreheat(opts?: { maxConcurrent?: number }): void {
    if (running || exhausted) return;
    // Node 桩兼容：测试环境没有 Image 全局
    if (typeof Image === 'undefined') { exhausted = true; return; }
    if (!queue) {
        const skip = new Set(warmed);
        if (activeUrl) skip.add(activeUrl);
        queue = Object.values(SCENE_BGS).filter(u => !skip.has(u));
    }
    if (queue.length === 0) { exhausted = true; return; }

    running = true;
    const batchSize = opts?.maxConcurrent ?? 2;
    let first = true;

    const pump = (): void => {
        if (!queue || queue.length === 0) {
            exhausted = true;
            running = false;
            return;
        }
        const batch = queue.splice(0, batchSize);
        let pending = batch.length;
        const settle = (): void => {
            pending -= 1;
            if (pending > 0) return;
            first = false;
            nextIdle(pump, first);
        };
        batch.forEach(url => {
            warmed.add(url); // 发起即登记：失败图也计入，不重试、不输出
            const img = new Image();
            img.fetchPriority = 'low';
            img.decoding = 'async';
            img.onload = settle;
            img.onerror = settle;
            img.src = url;
        });
    };

    nextIdle(pump, first);
}

/** @deprecated 别名：等宿主 applyScene 登记当前图后再启动，请直接调 `scheduleScenePreheat()` */
export function preheatSceneBgs(): void {
    scheduleScenePreheat();
}
